import { adminApi } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import {
  ApiOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BuildOutlined,
  ClusterOutlined,
  ControlOutlined,
  DashboardOutlined,
  DollarOutlined,
  ExperimentOutlined,
  EyeOutlined,
  FundOutlined,
  FundProjectionScreenOutlined,
  HistoryOutlined,
  LogoutOutlined,
  MenuOutlined,
  RobotOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  StopOutlined,
  SwapOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UserOutlined,
  UserSwitchOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App as AntdApp,
  Button,
  Dropdown,
  Form,
  Input,
  Layout,
  List,
  Menu,
  type MenuProps,
  Modal,
  Result,
  Tabs,
  Typography,
} from 'antd';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { canAccessAdminPath } from './admin-path-access';
import { type NavigationResponse, parseNavigationResponse } from './navigation-response';

const { Header, Sider, Content } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

const GROUP_ICONS: Record<string, ReactNode> = {
  commercial: <BankOutlined />,
  'ai-supply': <ApiOutlined />,
  'content-production': <AppstoreOutlined />,
  'agent-resources': <RobotOutlined />,
  'operations-release': <BuildOutlined />,
  'security-governance': <SafetyCertificateOutlined />,
  finance: <DollarOutlined />,
};

const NAV_ICONS: Record<string, ReactNode> = {
  '/': <DashboardOutlined />,
  '/ops-screen': <FundProjectionScreenOutlined />,
  '/operations': <ControlOutlined />,
  '/instances': <ClusterOutlined />,
  '/drama': <AppstoreOutlined />,
  '/drama/teams': <TeamOutlined />,
  '/drama/asset-moderation': <EyeOutlined />,
  '/resource-pool': <ClusterOutlined />,
  '/images': <AppstoreOutlined />,
  '/backup': <HistoryOutlined />,
  '/monitoring': <FundOutlined />,
  '/releases': <RocketOutlined />,
  '/skills': <AuditOutlined />,
  '/audit': <AuditOutlined />,
  '/account': <UserOutlined />,
  '/payment': <DollarOutlined />,
  '/mail': <ApiOutlined />,
  '/notifications': <ApiOutlined />,
  '/trial-applications': <UserOutlined />,
  '/content-safety': <StopOutlined />,
  '/ai-gateway': <ApiOutlined />,
  '/vendors': <BankOutlined />,
  '/channels': <SwapOutlined />,
  '/channel-usage': <FundOutlined />,
  '/video-supply': <TeamOutlined />,
  '/video-models': <VideoCameraOutlined />,
  '/migration': <SwapOutlined />,
  '/task-center': <UnorderedListOutlined />,
  '/feature-flags': <ExperimentOutlined />,
  '/admin-users': <UserSwitchOutlined />,
  '/finance': <DollarOutlined />,
};

/**
 * 运维控制台主框架：左侧导航（全为运维治理页）+ 顶栏（管理员菜单）+ 内容区。
 */
export function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { message } = AntdApp.useApp();
  const [navigation, setNavigation] = useState<NavigationResponse['groups']>([]);
  const [knownPaths, setKnownPaths] = useState<string[] | null>(null);
  const [navigationError, setNavigationError] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [securityOpen, setSecurityOpen] = useState(false);
  const [passwordForm] = Form.useForm();
  const [mfaForm] = Form.useForm();

  useEffect(() => {
    let active = true;
    adminApi
      .get<unknown>('/me/navigation')
      .then((response) => {
        if (active) {
          const navigationResponse = parseNavigationResponse(response.data);
          if (!navigationResponse) throw new Error('后台导航接口返回格式不兼容');
          setNavigation(navigationResponse.groups);
          setKnownPaths(navigationResponse.known_paths);
          setNavigationError(false);
        }
      })
      .catch(() => {
        if (active) {
          setNavigation([]);
          setKnownPaths(null);
          setNavigationError(true);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const navItems = useMemo<MenuItem[]>(() => {
    const items: MenuItem[] = [];
    for (const group of navigation) {
      const children = group.items.map((item) => ({
        key: item.href,
        icon: NAV_ICONS[item.href],
        label: <Link to={item.href}>{item.label}</Link>,
      }));
      if (group.key === 'workspace') items.push(...children);
      else {
        items.push({
          key: `group:${group.key}`,
          icon: GROUP_ICONS[group.key],
          label: group.label,
          children,
        });
      }
    }
    return items;
  }, [navigation]);

  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return navigation
      .flatMap((group) => group.items.map((item) => ({ ...item, groupLabel: group.label })))
      .filter((item) => {
        if (!query) return true;
        return `${item.label} ${item.groupLabel} ${item.href}`.toLowerCase().includes(query);
      });
  }, [navigation, searchQuery]);

  // 选中态：取最长匹配前缀（'/users' 优先于 '/'）。
  const selectedKey = useMemo(() => {
    const keys = navigation
      .flatMap((group) => group.items.map((item) => item.href))
      .sort((a, b) => b.length - a.length);
    return (
      keys.find((k) => (k === '/' ? location.pathname === '/' : location.pathname.startsWith(k))) ??
      location.pathname
    );
  }, [location.pathname, navigation]);

  const allowedPaths = useMemo(
    () => navigation.flatMap((group) => group.items.map((item) => item.href)),
    [navigation],
  );
  const pageAllowed =
    knownPaths !== null && canAccessAdminPath(location.pathname, knownPaths, allowedPaths);

  useEffect(() => {
    if (
      !navigationError &&
      knownPaths !== null &&
      location.pathname === '/' &&
      !allowedPaths.includes('/') &&
      allowedPaths[0]
    ) {
      navigate(allowedPaths[0], { replace: true });
    }
  }, [allowedPaths, knownPaths, location.pathname, navigate, navigationError]);
  const currentPage = useMemo(() => {
    for (const group of navigation) {
      const item = group.items.find((entry) => entry.href === selectedKey);
      if (item) return { title: item.label, group: group.label };
    }
    return { title: '运维工作台', group: 'DRAMA OPS' };
  }, [navigation, selectedKey]);

  const userMenu: MenuProps['items'] = [
    {
      key: 'security',
      icon: <SafetyCertificateOutlined />,
      label: '安全设置',
      onClick: () => setSecurityOpen(true),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: () => {
        logout();
        navigate('/login', { replace: true });
      },
    },
  ];

  return (
    <Layout className="admin-shell">
      <Sider
        className="admin-sidebar"
        width={240}
        collapsedWidth={isMobile ? 0 : 72}
        breakpoint="lg"
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        onBreakpoint={setIsMobile}
      >
        <div className="admin-sidebar__brand">
          <div className="admin-sidebar__mark">D</div>
          <div className="admin-sidebar__brand-copy">
            <span className="admin-sidebar__name">DramaClaw</span>
            <span className="admin-sidebar__meta">OPERATIONS</span>
          </div>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          defaultOpenKeys={navigation
            .filter((group) => group.key !== 'workspace')
            .map((group) => `group:${group.key}`)}
          items={navItems}
        />
      </Sider>
      <Layout className={`admin-main${collapsed && !isMobile ? ' admin-main--collapsed' : ''}`}>
        <Header className="admin-header">
          {isMobile && (
            <Button
              className="admin-mobile-menu"
              aria-label="打开导航"
              icon={<MenuOutlined />}
              onClick={() => setCollapsed(false)}
            />
          )}
          <div className="admin-header__context">
            <div className="admin-header__eyebrow">{currentPage.group}</div>
            <div className="admin-header__title">{currentPage.title}</div>
          </div>
          <div className="admin-header__actions">
            <Button
              className="admin-search-trigger"
              icon={<SearchOutlined />}
              onClick={() => setSearchOpen(true)}
            >
              搜索后台功能 <Typography.Text type="secondary">⌘ K</Typography.Text>
            </Button>
            <Dropdown menu={{ items: userMenu }} placement="bottomRight">
              <span className="admin-user-trigger">
                <span className="admin-user-trigger__avatar">
                  <UserOutlined />
                </span>
                <span className="admin-user-trigger__name">
                  {user?.name || user?.email || '管理员'}
                </span>
              </span>
            </Dropdown>
          </div>
        </Header>
        <Content className="admin-content">
          {navigationError && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="权限导航加载失败"
              description="后台服务可能不可用，请检查依赖健康状态后刷新页面。"
            />
          )}
          {!navigationError && knownPaths === null ? (
            <Result status="info" title="正在确认页面权限" />
          ) : !navigationError && !pageAllowed ? (
            <Result
              status="403"
              title="无权访问此功能"
              subTitle="当前管理员没有该业务域的权限。如需使用，请联系超级管理员授权。"
              extra={
                <Button
                  type="primary"
                  onClick={() => navigate(allowedPaths[0] ?? '/', { replace: true })}
                >
                  返回可用功能
                </Button>
              }
            />
          ) : !navigationError ? (
            <Outlet />
          ) : null}
        </Content>
      </Layout>
      {isMobile && !collapsed && (
        <button
          type="button"
          className="admin-sidebar-backdrop"
          aria-label="关闭导航"
          onClick={() => setCollapsed(true)}
        />
      )}
      <Modal
        open={searchOpen}
        className="admin-search-modal"
        title="搜索后台功能"
        footer={null}
        onCancel={() => {
          setSearchOpen(false);
          setSearchQuery('');
        }}
        afterOpenChange={(open) => {
          if (!open) setSearchQuery('');
        }}
      >
        <Input
          autoFocus
          allowClear
          prefix={<SearchOutlined />}
          placeholder="输入功能或业务域，例如：账号、视频供应、发布"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          onPressEnter={() => {
            const first = searchResults[0];
            if (!first) return;
            navigate(first.href);
            setSearchOpen(false);
          }}
        />
        <List
          style={{ marginTop: 12, maxHeight: 420, overflow: 'auto' }}
          locale={{ emptyText: '没有匹配且有权限访问的功能' }}
          dataSource={searchResults}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button
                  key="open"
                  type="link"
                  onClick={() => {
                    navigate(item.href);
                    setSearchOpen(false);
                  }}
                >
                  打开
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={NAV_ICONS[item.href]}
                title={item.label}
                description={item.groupLabel}
              />
            </List.Item>
          )}
        />
      </Modal>
      <Modal
        open={securityOpen}
        title="安全设置"
        footer={null}
        onCancel={() => setSecurityOpen(false)}
        destroyOnClose
      >
        <Tabs
          items={[
            {
              key: 'password',
              label: '修改应急密码',
              children: (
                <Form
                  form={passwordForm}
                  layout="vertical"
                  onFinish={async (values: { current_password: string; new_password: string }) => {
                    await adminApi.post('/auth/password', values);
                    message.success('密码已更新');
                    passwordForm.resetFields();
                  }}
                >
                  <Alert
                    type="info"
                    showIcon
                    message="仅适用于独立应急运维账号；统一账号密码请到账户中心修改。"
                    style={{ marginBottom: 12 }}
                  />
                  <Form.Item name="current_password" label="当前密码" rules={[{ required: true }]}>
                    <Input.Password />
                  </Form.Item>
                  <Form.Item
                    name="new_password"
                    label="新密码"
                    rules={[{ required: true, min: 12 }]}
                  >
                    <Input.Password />
                  </Form.Item>
                  <Form.Item
                    name="confirm"
                    label="确认新密码"
                    dependencies={['new_password']}
                    rules={[
                      { required: true },
                      ({ getFieldValue }) => ({
                        validator: (_, value) =>
                          value === getFieldValue('new_password')
                            ? Promise.resolve()
                            : Promise.reject(new Error('两次密码不一致')),
                      }),
                    ]}
                  >
                    <Input.Password />
                  </Form.Item>
                  <Button type="primary" onClick={() => passwordForm.submit()}>
                    修改密码
                  </Button>
                </Form>
              ),
            },
            {
              key: 'mfa',
              label: '解绑本人 MFA',
              children: (
                <Form
                  form={mfaForm}
                  layout="vertical"
                  onFinish={async (values: { totp: string }) => {
                    await adminApi.post('/auth/mfa/disable', values);
                    message.success('MFA 已解绑，下次敏感操作会要求重新绑定');
                    mfaForm.resetFields();
                  }}
                >
                  <Alert
                    type="warning"
                    showIcon
                    message="解绑需要当前动态验证码，操作会写入审计日志。"
                    style={{ marginBottom: 12 }}
                  />
                  <Form.Item
                    name="totp"
                    label="6 位动态验证码"
                    rules={[{ required: true, pattern: /^\d{6}$/ }]}
                  >
                    <Input maxLength={6} inputMode="numeric" />
                  </Form.Item>
                  <Button danger onClick={() => mfaForm.submit()}>
                    确认解绑
                  </Button>
                </Form>
              ),
            },
          ]}
        />
      </Modal>
    </Layout>
  );
}
